import React, { useState, useRef, useEffect } from 'react';
import { Lightbulb, X, ChevronLeft, ChevronRight } from 'lucide-react';

const TIPS = [
  {
    title: 'Paste your whole document',
    body: "Paste your entire document into any section, place the cursor where a new chapter should begin, and click Split Chapter in the toolbar — it instantly moves everything after the cursor into a new chapter.",
  },
  {
    title: 'Formatting is handled automatically',
    body: "No need to add a Table of Contents, page numbers, headers, or footers yourself. Once you Preview or submit your book, all of these are automatically applied in the generated PDF.",
  },
];

const TipsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setTipIndex(0);
    setOpen(o => !o);
  };

  const tip = TIPS[tipIndex];

  return (
    /* Sits above feedback button: support(24+52)+gap(12)+feedback(52)+gap(8) = 148px */
    <div ref={panelRef} style={{ position: 'fixed', bottom: 148, right: 24, zIndex: 60 }}>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 52,
          right: 0,
          width: 320,
          background: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          border: '1px solid #fde68a',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#fffbeb',
            borderBottom: '1px solid #fde68a',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lightbulb size={15} style={{ color: '#d97706' }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: '#92400e' }}>Tips</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex' }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Tip content */}
          <div style={{ padding: '16px 16px 12px' }}>
            <p style={{ fontWeight: 600, fontSize: 13, color: '#111827', margin: '0 0 8px' }}>
              {tip.title}
            </p>
            <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.65, margin: 0 }}>
              {tip.body}
            </p>
          </div>

          {/* Footer: arrows + dot indicator */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 16px 14px',
          }}>
            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: 5 }}>
              {TIPS.map((_, i) => (
                <div key={i} style={{
                  width: i === tipIndex ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === tipIndex ? '#f59e0b' : '#e5e7eb',
                  transition: 'width 0.2s',
                  cursor: 'pointer',
                }} onClick={() => setTipIndex(i)} />
              ))}
            </div>

            {/* Prev / Next arrows */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setTipIndex(i => Math.max(0, i - 1))}
                disabled={tipIndex === 0}
                style={{
                  background: tipIndex === 0 ? '#f3f4f6' : '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 7,
                  padding: '4px 8px',
                  cursor: tipIndex === 0 ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center',
                  color: tipIndex === 0 ? '#d1d5db' : '#d97706',
                  transition: 'all 0.15s',
                }}
              >
                <ChevronLeft size={15} />
              </button>
              <button
                onClick={() => setTipIndex(i => Math.min(TIPS.length - 1, i + 1))}
                disabled={tipIndex === TIPS.length - 1}
                style={{
                  background: tipIndex === TIPS.length - 1 ? '#f3f4f6' : '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 7,
                  padding: '4px 8px',
                  cursor: tipIndex === TIPS.length - 1 ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center',
                  color: tipIndex === TIPS.length - 1 ? '#d1d5db' : '#d97706',
                  transition: 'all 0.15s',
                }}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        id="editor-tips-btn"
        onClick={handleOpen}
        title="Tips"
        style={{
          width: 52, height: 52,
          borderRadius: '50%',
          background: open ? '#f59e0b' : '#ffffff',
          border: '1.5px solid #fde68a',
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s',
          color: open ? '#ffffff' : '#d97706',
        }}
      >
        <Lightbulb size={22} />
      </button>
    </div>
  );
};

export default TipsPanel;
